import React, { useState, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import moment from 'moment-timezone';
import {
	Grid,
	TextField,
	RadioGroup,
	FormControlLabel,
	Radio,
	Typography,
	FormControl,
} from '@mui/material';

const StyledLabel = styled(FormControlLabel)({
	fontFamily: 'Gotham',
	'.MuiFormControlLabel-label': {
		fontFamily: 'Gotham',
	},
});

const FiscalCalendarForm = ({ 
	startOfDay = '05:00', 
	startOfWeek = 'Sunday',
	values = {
		startOfYear: '01',
		calendarType: 'calendar',
		periodType: 12,
		periods: Array(13).fill(4),
	},
	onChange,
	errors = {}
}) => {
	const formValues = useMemo(() => ({
		startOfYear: values.startOfYear || '01',
		startOfDay: startOfDay,
		startOfWeek: startOfWeek,
		calendarType: values.calendarType || 'calendar',
		periodType: values.periodType || 12,
		periods: values.periods || Array(13).fill(4),
	}), [values.startOfYear, startOfDay, startOfWeek, values.calendarType, values.periodType, values.periods]);

	const [dateRanges, setDateRanges] = useState([]);

	useEffect(() => {
		const { startOfYear, periods, periodType } = formValues;
		const now = moment.tz(moment.tz.guess());
		const year = now.year();
		let startDate = moment.tz(`${year}-${startOfYear.replace('/', '-')}`, moment.tz.guess());
		if (formValues.calendarType === 'calendar') {
			const newRanges = [];
			const numPeriods = 12;
			for (let i = 0; i < numPeriods; i++) {
				const periodStart = moment(startDate);
				const periodEnd = moment(periodStart).add(1, 'month').subtract(1, 'day');
				newRanges.push(
					`${periodStart.format('M/D/YYYY')} - ${periodEnd.format('M/D/YYYY')}`
				);
				startDate = moment(periodEnd).add(1, 'day');
			}
			setDateRanges(newRanges);
		} else {
			const newRanges = periods.slice(0, periodType === '12' ? 12 : 13).map((weeks) => {
				const endDate = moment(startDate).add(weeks * 7 - 1, 'days');
				const range = `${startDate.format('M/D/YYYY')} - ${endDate.format('M/D/YYYY')}`;
				startDate = moment(endDate).add(1, 'day');
				return range;
			});
			setDateRanges(newRanges);
		}
	}, [formValues]);

	const handlePeriodChange = (index, value) => {
		const newPeriods = [...formValues.periods];
		newPeriods[index] = parseInt(value) || 0;
		onChange('fiscalPeriods', newPeriods);
	};

	const totalWeeks = formValues.periods
		.slice(0, formValues.periodType === 12 ? 12 : 13)
		.reduce((a, b) => a + b, 0);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} sx={{ pt: 0}}>
				<FormControl>
					<RadioGroup
						row
						name="calendarType"
						value={formValues.calendarType}
						onChange={(event) => onChange('fiscalCalendarType', event.target.value)}
					>
						<StyledLabel value="calendar" control={<Radio />} label="Calendar" />
						<StyledLabel value="fiscal" control={<Radio />} label="Fiscal Period" />
					</RadioGroup>
				</FormControl>
			</Grid>
			<Grid item xs={4}>
				<TextField
					name="startOfYear"
					required
					label={
						formValues.calendarType === 'calendar'
							? 'Start of Year (MM)'
							: 'Start of Year (MM/DD)'
					}
					value={formValues.startOfYear}
					onChange={(event) => {
						const val = event.target.value.replace(/[^0-9/]/g, '');
						let formatted = val;
						if (/^\d{3,}$/.test(val) && val[2] !== '/') {
							formatted = val.slice(0, 2) + '/' + val.slice(2, 4);
						}
						if (formValues.calendarType === 'calendar' && formatted.length > 2) {
							formatted = formatted.slice(0, 2);
						}
						onChange('fiscalStartOfYear', formatted);
					}}
					error={Boolean(errors.startOfYear)}
					helperText={errors.startOfYear}
					fullWidth
				/>
			</Grid>

			{formValues.calendarType === 'fiscal' && (
				<>
					<Grid item xs={12}>
						<FormControl>
							<RadioGroup
								row
								name="periodType"
								value={formValues.periodType}
								onChange={(event) => onChange('fiscalPeriodType', +event.target.value)}
							>
								<StyledLabel value={12} control={<Radio />} label="12 Periods" />
								<StyledLabel value={13} control={<Radio />} label="13 Periods" />
							</RadioGroup>
						</FormControl>
					</Grid>

					{[...Array(parseInt(formValues.periodType)).keys()].map((i) => (
						<Grid item xs={2} key={i}>
							<TextField
								label={`Period ${i + 1}`}
								type="number"
								inputProps={{ min: 0, max: 5 }}
								value={formValues.periods[i]}
								onChange={(e) => handlePeriodChange(i, e.target.value)}
								fullWidth
							/>
						</Grid>
					))}

					<Grid item xs={12}>
						<Typography fontFamily={'Gotham'} color={'rgb(0 0 0 / 60%)'} variant="body1">
							Total Weeks: {totalWeeks}
						</Typography>
						{totalWeeks < 52 || totalWeeks > 53 ? (
							<Typography fontFamily={'Gotham'} color="error">
								Total weeks must be 52 or 53
							</Typography>
						) : null}
					</Grid>
				</>
			)}
			<Grid item xs={12}>
				<Typography fontFamily={'Gotham'} color={'rgb(0 0 0 / 60%)'} variant="h6">
					Auto-Calculated Date Ranges
				</Typography>
				<ul style={{ listStyle: 'none', marginLeft: 24, paddingLeft: 0 }}>
					{dateRanges.map((range, idx) => (
						<li key={idx}>
							<b>Period {idx + 1}</b>: {range}
						</li>
					))}
				</ul>
			</Grid>
		</Grid>
	);
};

export default FiscalCalendarForm;
